import { goSolidApi } from "@/composables/useGoSolidApi";
import type {
  ArrDetail,
  CountryDetail,
  CountrySide,
  ArrResponse,
  DashboardFilters,
  DashboardResponse,
  MonthDetail,
  RetentionDetail,
  RetentionResponse,
} from "./financialDashboardTypes";

const BASE = "/api/admin/financial-dashboard";

const filterParams = (filters: DashboardFilters): URLSearchParams => {
  const params = new URLSearchParams();
  params.set("date_from", filters.dateFrom);
  params.set("date_to", filters.dateTo);
  filters.countryCodes.forEach((code) => params.append("country_codes", code));
  return params;
};

const monthParams = (
  year: number,
  month: number,
  countryCodes: string[]
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set("year", String(year));
  params.set("month", String(month));
  countryCodes.forEach((code) => params.append("country_codes", code));
  return params;
};

/**
 * Admin financial dashboard endpoints (`app/money/admin/api/admin_financial_dashboard_controller.py`).
 * Every call returns the raw axios response; errors propagate to the caller.
 */
export const financialDashboardApi = {
  /** Summary, monthly rows, country rows and the revenue-stream taxonomy for the filter range. */
  getAll: (filters: DashboardFilters) =>
    goSolidApi.get<DashboardResponse>(`${BASE}/all?${filterParams(filters)}`),

  /** Transactions and standalone items behind one month's figures. */
  getMonthDetail: (year: number, month: number, countryCodes: string[]) =>
    goSolidApi.get<MonthDetail>(
      `${BASE}/month-detail?${monthParams(year, month, countryCodes)}`
    ),

  /** Sales (buyer side) or sourcing companies and sales (supply side) behind one country row. */
  getCountryDetail: (
    side: CountrySide,
    country: string,
    filters: DashboardFilters
  ) => {
    const params = filterParams(filters);
    params.set("side", side);
    params.set("country", country);
    return goSolidApi.get<CountryDetail>(`${BASE}/country-detail?${params}`);
  },

  /**
   * Month-by-month forward-looking ARR. `goPrice` (EUR/MWh) overrides the derived price
   * assumption; pass null to let the backend derive it from recent sales.
   */
  getArr: (filters: DashboardFilters, goPrice: number | null) => {
    const params = filterParams(filters);
    if (goPrice !== null) params.set("go_price", String(goPrice));
    return goSolidApi.get<ArrResponse>(`${BASE}/arr?${params}`);
  },

  /** Per-company, per-technology and per-country breakdown behind one month's ARR. */
  getArrDetail: (
    year: number,
    month: number,
    countryCodes: string[],
    goPrice: number | null
  ) => {
    const params = monthParams(year, month, countryCodes);
    if (goPrice !== null) params.set("go_price", String(goPrice));
    return goSolidApi.get<ArrDetail>(`${BASE}/arr-detail?${params}`);
  },

  /** Month-by-month net / gross revenue retention over a trailing window of `windowMonths`. */
  getRetention: (filters: DashboardFilters, windowMonths: number) => {
    const params = filterParams(filters);
    params.set("window_months", String(windowMonths));
    return goSolidApi.get<RetentionResponse>(`${BASE}/retention?${params}`);
  },

  /** Per-customer bridge behind one month's NRR. */
  getRetentionDetail: (
    year: number,
    month: number,
    windowMonths: number,
    countryCodes: string[]
  ) => {
    const params = monthParams(year, month, countryCodes);
    params.set("window_months", String(windowMonths));
    return goSolidApi.get<RetentionDetail>(
      `${BASE}/retention-detail?${params}`
    );
  },
};
